/**
 * TruePrice Design Tokens — Style Guide v1.0
 *
 * Single source of truth for all visual styling.
 * Import `T` everywhere. No hardcoded hex, no magic numbers.
 */

// ── Canonical Token Object ──────────────────────────────────────

export const T = {
  // ─── Colors: Canvas Sage Palette ───────────────────────────────
  cream:          '#FAF8F3',
  white:          '#FFFFFF',
  primary:        '#3DABA5',
  primaryDark:    '#2A9089',
  primarySubtle:  'rgba(61,171,165,0.06)',
  primaryGlow:    'rgba(61,171,165,0.18)',
  text:           '#1A1A1A',
  textSecondary:  '#6B6B6B',
  textMuted:      '#A8A5A0',
  border:         '#E8E5DE',
  borderLight:    '#F0EDE6',
  navy:           '#2C3E50',

  // ─── Extended Palette (Calculator Wizard) ────────────────────
  cream100:       '#F5F2EB',
  cream200:       '#F0EDE6',
  tealSoft:       '#E8F5F4',
  tealGlass:      'rgba(61,171,165,0.08)',
  tealGlow:       'rgba(61,171,165,0.18)',
  charcoal800:    '#2A2826',
  charcoal700:    '#3A3835',
  charcoal600:    '#4A4843',
  charcoal500:    '#5A5752',
  charcoal400:    '#7A7670',
  charcoal300:    '#9E9A93',
  charcoal200:    '#C8C5BF',
  orangeBg:       '#FEF3EB',
  red:            '#BD504B',

  // ─── Extended Category Colors ──────────────────────────────────
  tealDeep:       '#166B66',
  purple:         '#6366F1',
  green:          '#2D7F3E',
  sand:           '#F5F0E8',

  // ─── Gray Scale (Tailwind-aligned) ───────────────────────────
  gray50:         '#F9FAFB',
  gray100:        '#F3F4F6',
  gray200:        '#E5E7EB',
  gray300:        '#D1D5DB',
  gray400:        '#9CA3AF',
  gray500:        '#6B7280',
  gray600:        '#4B5563',

  // ─── Semantic Status ───────────────────────────────────────────
  success:        '#3DABA5',
  warning:        '#E07A3D',
  danger:         '#D64545',

  // ─── Shadows (3 tiers) ────────────────────────────────────────
  shadowFlat:     'none',
  shadowRaised:   '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
  shadowFloating: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',

  // ─── Spacing (base 4px) ───────────────────────────────────────
  xs:   '4px',
  sm:   '8px',
  md:   '12px',
  lg:   '16px',
  xl:   '20px',
  '2xl':'24px',
  '3xl':'32px',
  '4xl':'48px',

  // ─── Border Radius (4 values) ─────────────────────────────────
  radiusSm:   '8px',
  radiusMd:   '12px',
  radiusLg:   '16px',
  radiusPill:  '26px',

  // ─── Typography ────────────────────────────────────────────────
  fontFamily: '"Manrope", "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontNumeric: '"Manrope", "DM Sans", -apple-system, sans-serif',

  // ─── Surfaces ──────────────────────────────────────────────────
  accordionBg:    '#F5F0E8',

  // ─── Animation ─────────────────────────────────────────────────
  easeOut: 'cubic-bezier(0.32, 0.72, 0, 1)',
  easeEntrance: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

// ── Avatar Color Cycle ──────────────────────────────────────────

export const AVATAR_COLORS = [
  '#3DABA5', // Teal
  '#E07A3D', // Orange
  '#C69026', // Gold
  '#2A9089', // Dark teal
  '#5B8DEF', // Blue
] as const;

// ── Health Badge Utility ────────────────────────────────────────

export function healthBadge(status: 'strong' | 'maintain' | 'weak') {
  const config = {
    strong:   { bg: 'rgba(61,171,165,0.1)',  color: T.primary },
    maintain: { bg: 'rgba(224,122,61,0.1)',   color: T.warning },
    weak:     { bg: 'rgba(214,69,69,0.1)',    color: T.danger },
  }[status];

  return {
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: config.bg,
    color: config.color,
  } as const;
}

// ── Backward-Compatible Exports (used by 30+ files) ─────────────
// TODO: Migrate consumers to T.* and remove these

export const GRADIENTS = {
  tealHero: `linear-gradient(to bottom, ${T.primary} 0%, ${T.primaryDark} 100%)`,
};

export const GLASS = {
  action: {
    backgroundColor: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(4px)',
  },
  kpiCard: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.25)',
  },
};

export const COLORS = {
  cream: T.cream,
  white: T.white,
  teal: T.primary,
  tealDark: T.primaryDark,
  tealSoft: '#5BBFBA',
  tealSubtle: T.primarySubtle,
  tealGlow: T.primaryGlow,
  tealGlass: 'rgba(61,171,165,0.08)',
  coral: '#E8735A',
  ink: T.text,
  inkMedium: '#3A3A3A',
  inkSoft: T.textSecondary,
  inkFaint: T.textMuted,
  inkGhost: '#C8C5BF',
  border: T.border,
  borderLight: T.borderLight,
};

export const NAVY_DARK = '#1B2838';
export const NAVY = '#243A4E';
export const NAVY_LIGHT = '#2A3F55';
export const HERO_GRADIENT = 'linear-gradient(170deg, #1B2838 0%, #243A4E 55%, #2A3F55 100%)';
export const HERO_SHADOW = '0 6px 28px rgba(27,40,56,0.35)';
export const HERO_SHADOW_DEEP = '0 8px 36px rgba(27,40,56,0.5)';
export const GLASS_BG = 'rgba(255,255,255,0.1)';
export const GLASS_BG_HOVER = 'rgba(255,255,255,0.18)';

export const TYPOGRAPHY = {
  fontFamily: T.fontFamily,
  fontNumeric: T.fontNumeric,
  fontHeading: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
};

export const TYPE_SCALE = {
  h1: {
    fontFamily: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
    fontSize: 24,
    fontWeight: 700 as const,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h2: {
    fontFamily: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
    fontSize: 18,
    fontWeight: 600 as const,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  body: {
    fontFamily: T.fontFamily,
    fontSize: 14,
    fontWeight: 400 as const,
    lineHeight: 1.45,
  },
  caption: {
    fontFamily: T.fontFamily,
    fontSize: 13,
    fontWeight: 400 as const,
    lineHeight: 1.4,
    color: T.textSecondary,
  },
  label: {
    fontFamily: T.fontFamily,
    fontSize: 12,
    fontWeight: 600 as const,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
  },
  hero: {
    fontFamily: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
    fontSize: 56,
    fontWeight: 800 as const,
    letterSpacing: -2,
    lineHeight: 1,
  },
  kpi: {
    fontFamily: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
    fontSize: 28,
    fontWeight: 700 as const,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  },
};

export const SPACING = {
  baseUnit: 4,
  touchTarget: 44,
};

export const BRAND = {
  cream: T.cream,
  creamDark: T.borderLight,
  white: T.white,
  ink: T.text,
  inkLight: T.textSecondary,
  inkFaint: '#B8B5AD',
  teal: T.primary,
  tealDark: T.primaryDark,
  tealLight: '#E8F5F4',
  coral: '#E8735A',
  coralLight: '#FDE8E4',
  gold: '#C4A235',
  goldWarm: '#D4A843',
  fontSans: T.fontFamily,
  fontHeading: '"Manrope", "Satoshi", "DM Sans", -apple-system, sans-serif',
  fontSerif: '"Fraunces", Georgia, serif',
};

export const BRAND_IMAGES = {
  hero: '/brand/hero-strokes.png',
  career: '/brand/onboarding-career.png',
  burn: '/brand/onboarding-burn.png',
  hours: '/brand/onboarding-hours.png',
  location: '/brand/onboarding-location.png',
  complete: '/brand/onboarding-complete.png',
};
